/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { ConfirmDeleteDialogData, ConfirmDialogService } from '@epg/ng-components';
import { firstValueFrom } from 'rxjs';
import { TaskDataImage, TaskDatumType } from '../../models/task.model';
import { COMPANY_NAME_QUERY_PARAM, TENANT_QUERY_PARAM } from '../../services/api.service';
import { ACTIVE_TENANT_VARIABLE } from '../../services/tenant-mapping.service';
import { EPGImageSliderItem, EPGImageFormat } from './epg-image-slider/epg-image-slider.component';
import { TitleCasePipe } from '@angular/common';

export function getImageSliderFromTaskDatum(imageData: TaskDataImage): EPGImageSliderItem {
  let mimeType = imageData.type === TaskDatumType.IMAGE_PNG ? 'image/png' : 'image/jpeg';
  if (imageData.type.toString() == 'IMAGE_URL') {
    let url = attachCompanyNameToUrl(imageData.value);
    const separator = url.includes('?') ? '&' : '?';
    if (hasUUIDInUrl(url)) {
      url = `${url}${separator}returnType=direct`;
    }
    return {
      image: url,
      imageFormat: EPGImageFormat.URL,
      rotationValue: 0,
      scaleValue: 1
    };
  }
  return {
    image: `data:${mimeType};base64,${imageData.value}`,
    imageFormat: EPGImageFormat.BASE64,
    rotationValue: 0,
    scaleValue: 1
  };
}

export function attachCompanyNameToUrl(url: string): string {
  const tenant = getActiveTenantFromSessionStorage();
  return tenant ? `${url}?${getCompanyQueryParameter(url)}=${tenant}` : url;
}

function getCompanyQueryParameter(url: string) {
  // Determine the appropriate query parameter based on the URL
  // Blob service uses 'tenant', while OneApp API uses 'company_name' to select the tenant
  return hasUUIDInUrl(url) ? TENANT_QUERY_PARAM : COMPANY_NAME_QUERY_PARAM;
}

export function hasUUIDInUrl(url: string): boolean {
  const regex = /([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})/i;
  return regex.test(url);
}

export function getActiveTenantFromSessionStorage(): string | undefined {
  return sessionStorage.getItem(ACTIVE_TENANT_VARIABLE) ?? undefined;
}

export function isBase64Image(data: string): boolean {
  // Check for data URL prefix
  const base64Pattern = /^data:image\/(jpeg|png|gif|bmp|webp);base64,/;
  if (!base64Pattern.test(data)) {
    return false;
  }

  // Extract the base64 part of the string
  const base64Data = data.split(',')[1];

  // Check if the base64 data is valid
  const base64Regex = /^[A-Za-z0-9+/]+={0,2}$/;
  return base64Regex.test(base64Data);
}

/**
 * Determines the position (first, second, third, fourth and last) of a weekday in its month
 * @param date The date to analyze
 * @returns String position word (first, second, third, fourth, or last)
 */
export function getWeekdayPositionInMonth(date: Date): string {
  const dayOfMonth = date.getDate();
  const dayOfWeek = date.getDay();

  // Find the date of the first occurrence of this weekday in the month
  const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const firstDayOfWeekday = new Date(firstDayOfMonth);

  // Calculate how many days to add to get to the first occurrence of this weekday
  const dayDiff = (dayOfWeek - firstDayOfMonth.getDay() + 7) % 7;
  firstDayOfWeekday.setDate(1 + dayDiff);

  // Calculate which occurrence of the weekday this date represents
  // (e.g., 1st Monday, 2nd Monday, etc.)
  const weekNumber = Math.ceil((dayOfMonth - firstDayOfWeekday.getDate() + 1) / 7);
  const position = weekNumber + 1;

  // Calculate total occurrences of this weekday in the month
  const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const totalWeeks = Math.ceil((lastDayOfMonth - firstDayOfWeekday.getDate() + 1) / 7);

  // Now correctly determine the position (accounting for 0-index to 1-index conversion)
  const actualPosition = Math.floor((dayOfMonth - firstDayOfWeekday.getDate()) / 7) + 1;

  // Determine the position word
  const positionWords = ['first', 'second', 'third', 'fourth'];

  // Return the appropriate position word or 'last' if beyond fourth
  return actualPosition <= positionWords.length ? positionWords[actualPosition - 1] : 'last';
}

/**
 * Gets the text for the last position option (e.g., "last Monday")
 */
export function getLastPositionText(scheduleDate: string, locale: string): string {
  const date = new Date(scheduleDate!);
  const weekdayName = date.toLocaleString(locale, { weekday: 'long' });
  return $localize`:Last weekday position@@summaryLastWeekday:last ${weekdayName}`;
}

/**
 * Gets the text for the standard position-based option (e.g., "third Monday")
 */
export function getPositionText(scheduleDate: string, locale: string): string {
  const date = new Date(scheduleDate!);
  const position = getWeekdayPositionInMonth(date);
  const weekdayName = date.toLocaleString(locale, { weekday: 'long' });

  // Use different templates based on the position for better translation possibilities
  if (position === 'first') {
    return $localize`:First weekday position@@summaryFirstWeekday:first ${weekdayName}`;
  } else if (position === 'second') {
    return $localize`:Second weekday position@@summarySecondWeekday:second ${weekdayName}`;
  } else if (position === 'third') {
    return $localize`:Third weekday position@@summaryThirdWeekday:third ${weekdayName}`;
  } else if (position === 'fourth') {
    // We'll handle fourth weekdays based on the selected radio option
    return $localize`:Fourth weekday position@@summaryFourthWeekday:fourth ${weekdayName}`;
  } else {
    return `${position} ${weekdayName}`;
  }
}

/**
 * Gets the text for the date-based option (e.g., "16th")
 * This method properly supports localization for ordinal numbers
 */
export function getDateText(scheduleDate: string): string {
  const date = new Date(scheduleDate!);
  const dayOfMonth = date.getDate();

  // For English, we would add ordinal suffixes (1st, 2nd, 3rd, etc.)
  // But for proper localization, we'll use a separate tag for each number range
  // This allows translators to completely customize the format based on language rules

  if (dayOfMonth === 1) {
    return $localize`:1st day of month@@summaryDayOfMonth1: 1st`;
  } else if (dayOfMonth === 2) {
    return $localize`:2nd day of month@@summaryDayOfMonth2: 2nd`;
  } else if (dayOfMonth === 3) {
    return $localize`:3rd day of month@@summaryDayOfMonth3: 3rd`;
  } else if (dayOfMonth === 4) {
    return $localize`:4th day of month@@summaryDayOfMonth4: 4th`;
  } else if (dayOfMonth >= 5 && dayOfMonth <= 20) {
    // 5th-20th all use 'th' suffix in English
    return $localize`:th day of month@@summaryDayOfMonth: ${dayOfMonth}th`;
  } else if (dayOfMonth === 21) {
    return $localize`:21st day of month@@summaryDayOfMonth21: 21st`;
  } else if (dayOfMonth === 22) {
    return $localize`:22nd day of month@@summaryDayOfMonth22: 22nd`;
  } else if (dayOfMonth === 23) {
    return $localize`:23rd day of month@@summaryDayOfMonth23: 23rd`;
  } else if (dayOfMonth === 31) {
    return $localize`:31st day of month@@summaryDayOfMonth31: 31st`;
  } else {
    // All other days (24-30) use 'th' suffix in English
    return $localize`:th day of month@@summaryDayOfMonth: ${dayOfMonth}th`;
  }
}
/**
 * Gets a description of the yearly repetition pattern based on the scheduled date
 */
export function getYearlyDescription(date: Date, locale: string): string {
  const month = date.toLocaleString(locale, { month: 'long' });
  const dayOfMonth = date.getDate();

  // Provide both options for better understanding
  return `${month} ${dayOfMonth} `;
}
export async function opencanDeactivateDialog(
  dialogService: ConfirmDialogService
): Promise<boolean> {
  const title = $localize`:confirm leave header@@confirmLeave:You have unsaved changes!`;
  const msg = $localize`:leave confirmation text@@leaveConfirmation:By leaving this page you will permanently lose your form changes.`;
  const confirmButtonText = $localize`:button text cancel@@buttonDiscard:DISCARD CHANGES`;
  const cancelButtonText = $localize`:button text leave@@buttonContinue:CONTINUE EDITING`;
  const dialogData: ConfirmDeleteDialogData = {
    itemName: '',
    title: title,
    message: msg,
    confirmText: toTitleCase(confirmButtonText),
    cancelText: toTitleCase(cancelButtonText)
  };
  return await firstValueFrom(dialogService.openConfirmDelete(dialogData));
}

export function toTitleCase(text: string): string {
  return new TitleCasePipe().transform(text) || text;
}

export function capitalizeFirstLetter(input: string): string {
  if (!input) return input; // Return the input if it's empty or undefined
  return input.charAt(0).toUpperCase() + input.slice(1);
}
